import { useEffect, useRef } from "react";

/**
 * Runoff Mobile Detail — Hero
 *
 * Verbatim copy of samples/runoff-mobile-detail/src/components/Hero.jsx,
 * embedded here so HandoffDemo's Runoff photo zone shows the real,
 * animated water-bead hero instead of a static placeholder. Zero
 * dependencies beyond React, so it ports as-is with no adaptation.
 *
 * A canvas-based water-bead physics simulation: beads spawn, fall under
 * gravity, drift toward the cursor, leave a fading sheeting trail, and
 * produce a real splash ring on impact. Respects prefers-reduced-motion
 * (static sheet lines, no animation loop) and pauses the render loop
 * entirely when scrolled off-screen.
 */
export default function RunoffHeroPreview() {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const rafRef = useRef(null);
  const runningRef = useRef(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    function resize() {
      const w = wrap.clientWidth;
      const h = wrap.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    resize();
    window.addEventListener("resize", resize);

    // Static, motion-free fallback
    if (reduced) {
      const w = wrap.clientWidth, h = wrap.clientHeight;
      ctx.strokeStyle = "rgba(47,168,216,0.35)";
      ctx.lineWidth = 2;
      for (let i = 0; i < 7; i++) {
        const x = 60 + i * (w / 7);
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
      }
      return () => window.removeEventListener("resize", resize);
    }

    let mouseX = wrap.clientWidth * 0.72;
    function onMove(e) {
      const r = wrap.getBoundingClientRect();
      mouseX = e.clientX - r.left;
    }
    wrap.addEventListener("mousemove", onMove);

    // Pause the loop entirely when off-screen — don't burn cycles on a
    // hero nobody is looking at.
    const observer = new IntersectionObserver(
      (entries) => {
        runningRef.current = entries[0]?.isIntersecting ?? true;
        if (runningRef.current && !rafRef.current) {
          rafRef.current = requestAnimationFrame(tick);
        }
      },
      { threshold: 0 }
    );
    observer.observe(wrap);

    const beads = [];
    const splashes = [];
    const maxBeads = 55;
    let frame = 0;

    function spawnBead() {
      if (beads.length >= maxBeads) return;
      const w = wrap.clientWidth;
      const big = Math.random() < 0.22;
      beads.push({
        x: w * 0.5 + (Math.random() - 0.25) * w * 0.65,
        y: -14,
        r: big ? 4 + Math.random() * 3.5 : 1.6 + Math.random() * 2,
        vy: big ? 1.4 + Math.random() * 0.8 : 0.8 + Math.random() * 0.7,
        vx: 0,
        life: 0,
        trail: [],
      });
    }

    function tick() {
      if (!runningRef.current) {
        rafRef.current = null;
        return;
      }
      frame++;
      if (frame % 5 === 0) spawnBead();
      if (frame % 5 === 2) spawnBead();

      const w = wrap.clientWidth;
      const h = wrap.clientHeight;
      ctx.clearRect(0, 0, w, h);

      for (let i = splashes.length - 1; i >= 0; i--) {
        const s = splashes[i];
        s.r += 1.6;
        s.a -= 0.045;
        if (s.a <= 0) {
          splashes.splice(i, 1);
          continue;
        }
        ctx.beginPath();
        ctx.strokeStyle = `rgba(143,224,255,${s.a})`;
        ctx.lineWidth = 1.4;
        ctx.ellipse(s.x, s.y, s.r, s.r * 0.32, 0, 0, Math.PI * 2);
        ctx.stroke();
      }

      for (let i = beads.length - 1; i >= 0; i--) {
        const b = beads[i];
        const pull = (mouseX - b.x) * 0.001;
        b.vx += pull;
        b.vx *= 0.93;
        b.vy += 0.045;
        b.x += b.vx;
        b.y += b.vy;
        b.life++;

        b.trail.push({ x: b.x, y: b.y });
        if (b.trail.length > 50) b.trail.shift();

        ctx.beginPath();
        ctx.strokeStyle = `rgba(47,168,216,${Math.min(0.55, b.trail.length / 90)})`;
        ctx.lineWidth = Math.max(1, b.r * 0.5);
        b.trail.forEach((p, idx) => {
          if (idx === 0) ctx.moveTo(p.x, p.y);
          else ctx.lineTo(p.x, p.y);
        });
        ctx.stroke();

        ctx.beginPath();
        ctx.fillStyle = "#2FA8D8";
        ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.fillStyle = "rgba(255,255,255,0.65)";
        ctx.arc(b.x - b.r * 0.32, b.y - b.r * 0.32, b.r * 0.4, 0, Math.PI * 2);
        ctx.fill();

        if (b.y > h - 6) {
          splashes.push({ x: b.x, y: h - 4, r: 2, a: 0.7 });
          beads.splice(i, 1);
        } else if (b.life > 700) {
          beads.splice(i, 1);
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("resize", resize);
      wrap.removeEventListener("mousemove", onMove);
      observer.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <section
      ref={wrapRef}
      style={{
        position: "relative",
        background: "#071A24",
        overflow: "hidden",
        fontFamily: "'Inter', sans-serif",
        width: "100%",
        height: "100%",
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          display: "block",
        }}
      />

      <div
        style={{
          position: "relative",
          zIndex: 1,
          padding: "70px 40px",
          minHeight: 440,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 11,
            letterSpacing: "0.14em",
            color: "#2FA8D8",
            marginBottom: 16,
          }}
        >
          // MOBILE DETAILING · FRONT RANGE, CO
        </div>

        <h1
          style={{
            fontFamily: "'Cormorant', serif",
            fontWeight: 700,
            color: "#F3F7F8",
            fontSize: 76,
            lineHeight: 0.92,
            margin: "0 0 24px",
            maxWidth: 600,
            letterSpacing: "-0.01em",
          }}
        >
          Runoff clean.
          <br />
          Right in your
          <br />
          <span style={{ color: "#8FE0FF" }}>driveway.</span>
        </h1>

        <p
          style={{
            fontSize: 16,
            color: "#9FB6C0",
            maxWidth: 400,
            margin: "0 0 30px",
          }}
        >
          We bring the water, the tools, and twelve years of doing this
          right — wherever the car actually is.
        </p>

        <div style={{ display: "flex", gap: 12 }}>
          <button
            style={{
              background: "#2FA8D8",
              color: "#071A24",
              border: "none",
              padding: "14px 28px",
              borderRadius: 6,
              fontFamily: "'Inter', sans-serif",
              fontSize: 15,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Book a detail
          </button>
          <button
            style={{
              background: "transparent",
              border: "1px solid #2FA8D8",
              color: "#8FE0FF",
              padding: "14px 28px",
              borderRadius: 6,
              fontFamily: "'Inter', sans-serif",
              fontSize: 15,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            See pricing
          </button>
        </div>
      </div>
    </section>
  );
}
